import { useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

// The drag handle's wiring, handed to a card's memoised content
export interface DragHandle {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}

export const sameHandle = (a: DragHandle, b: DragHandle) =>
  a.attributes === b.attributes &&
  a.listeners === b.listeners &&
  a.setActivatorNodeRef === b.setActivatorNodeRef;

// useSortable for cards that split into a thin drag wrapper and memoised
// content. Every sortable item re-renders when its list changes (any filter),
// so the wrapper takes that hit and the content only redraws when what it
// shows changes. The drag position comes back as plain style, so moving a
// card never re-runs its styles.
export const useSortableCard = (id: string, disabled: boolean, data?: Record<string, unknown>) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled, data });

  // dnd-kit hands over a new listeners object every render; these keep one
  // identity (per set of events) and always call the latest handlers
  const listenersRef = useRef(listeners);
  listenersRef.current = listeners;
  const events = listeners ? Object.keys(listeners).join(',') : '';
  const stableListeners = useMemo(
    () =>
      events
        ? Object.fromEntries(
            events.split(',').map((name) => [name, (event: unknown) => listenersRef.current?.[name]?.(event)])
          )
        : undefined,
    [events]
  );

  const handle = useMemo<DragHandle>(
    () => ({ attributes, listeners: stableListeners, setActivatorNodeRef }),
    [attributes, stableListeners, setActivatorNodeRef]
  );

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 'auto',
  };

  return { setNodeRef, style, handle };
};
